/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';
import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';
import {
  Primitive,
  PrimitiveType,
} from '@yorkie-js/sdk/src/document/crdt/primitive';
import {
  removeDecimal,
  bigintFromBytesLE,
  bigintToBytesLE,
  bigintToInt32,
} from '@yorkie-js/sdk/src/util/number';
import type * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { DataSize } from '@yorkie-js/sdk/src/util/resource';
import { HLL } from '@yorkie-js/sdk/src/document/crdt/hll';

export enum CounterType {
  Int,
  Long,
  IntDedup,
}

export type CounterValue = number | bigint;

/**
 * `CRDTCounter` is a CRDT implementation of a counter. It is used to represent
 * a number that can be incremented or decremented.
 */
export class CRDTCounter extends CRDTElement {
  private valueType: CounterType;
  private value: CounterValue;
  private hll?: HLL;

  constructor(
    valueType: CounterType,
    value: CounterValue,
    createdAt: TimeTicket,
  ) {
    super(createdAt);
    this.valueType = valueType;
    switch (valueType) {
      case CounterType.IntDedup:
        this.value = 0;
        break;
      case CounterType.Int:
        if (typeof value === 'number') {
          if (value > Math.pow(2, 31) - 1 || value < -Math.pow(2, 31)) {
            this.value = bigintToInt32(BigInt(value));
          } else {
            this.value = removeDecimal(value);
          }
        } else {
          this.value = bigintToInt32(value);
        }
        break;
      case CounterType.Long:
        if (typeof value === 'number') {
          this.value = BigInt.asIntN(64, BigInt(Math.trunc(value)));
        } else {
          this.value = BigInt.asIntN(64, value);
        }
        break;
      default:
        throw new YorkieError(
          Code.ErrUnimplemented,
          `unimplemented type: ${valueType}`,
        );
    }
    if (this.isDedup()) {
      this.hll ??= new HLL();
    }
  }

  /**
   * `of` creates a new instance of Counter.
   */
  public static create(
    valueType: CounterType,
    value: CounterValue,
    createdAt: TimeTicket,
  ): CRDTCounter {
    return new CRDTCounter(valueType, value, createdAt);
  }

  /**
   * `valueFromBytes` parses the given bytes into value.
   */
  public static valueFromBytes(
    counterType: CounterType,
    bytes: Uint8Array,
  ): CounterValue {
    switch (counterType) {
      case CounterType.Int:
      case CounterType.IntDedup:
        return bytes[0] | (bytes[1] << 8) | (bytes[2] << 16) | (bytes[3] << 24);
      case CounterType.Long:
        return bigintFromBytesLE(bytes);
      default:
        throw new YorkieError(
          Code.ErrUnimplemented,
          `unimplemented type: ${counterType}`,
        );
    }
  }

  /**
   * `getDataSize` returns the data usage of this element.
   */
  public getDataSize(): DataSize {
    let data =
      this.valueType === CounterType.Int ||
      this.valueType === CounterType.IntDedup
        ? 4
        : 8;
    if (this.isDedup() && this.hll) {
      data += this.hll.toBytes().length;
    }
    return {
      data,
      meta: this.getMetaUsage(),
    };
  }

  /**
   * `toJSON` returns the JSON encoding of the value.
   */
  public toJSON(): string {
    return `${this.value}`;
  }

  /**
   * `toSortedJSON` returns the sorted JSON encoding of the value.
   */
  public toSortedJSON(): string {
    return this.toJSON();
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    return {
      createdAt: this.getCreatedAt().toTestString(),
      value: this.value,
      type: 'YORKIE_COUNTER',
    };
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  public deepcopy(): CRDTCounter {
    const clone = CRDTCounter.create(
      this.valueType,
      this.value,
      this.getCreatedAt(),
    );
    clone.setRemovedAt(this.getRemovedAt());
    clone.setMovedAt(this.getMovedAt());
    if (this.isDedup() && this.hll) {
      clone.restoreHLL(this.hll.toBytes());
    }
    return clone;
  }

  /**
   * `getType` returns the type of the value.
   */
  public getType(): CounterType {
    return this.valueType;
  }

  /**
   * `getCounterType` returns counter type of given value.
   */
  public static getCounterType(value: CounterValue): CounterType | undefined {
    switch (typeof value) {
      case 'bigint':
        return CounterType.Long;
      case 'number':
        if (value > Math.pow(2, 31) - 1 || value < -Math.pow(2, 31)) {
          return CounterType.Long;
        } else {
          return CounterType.Int;
        }
      default:
        return;
    }
  }

  /**
   * `isSupport` check if there is a counter type of given value.
   */
  public static isSupport(value: CounterValue): boolean {
    return !!CRDTCounter.getCounterType(value);
  }

  /**
   * `isInteger` checks if the num is integer.
   */
  public static isInteger(num: number): boolean {
    return num % 1 === 0;
  }

  /**
   * `isNumericType` check numeric type by JSONCounter.
   */
  public isNumericType(): boolean {
    const t = this.valueType;
    return (
      t === CounterType.Int ||
      t === CounterType.Long ||
      t === CounterType.IntDedup
    );
  }

  /**
   * `getValueType` get counter value type.
   */
  public getValueType(): CounterType {
    return this.valueType;
  }

  /**
   * `getValue` get counter value.
   */
  public getValue(): CounterValue {
    return this.value;
  }

  /**
   * `toBytes` creates an array representing the value.
   */
  public toBytes(): Uint8Array {
    switch (this.valueType) {
      case CounterType.Int:
      case CounterType.IntDedup: {
        const intVal = this.value as number;
        return new Uint8Array([
          intVal & 0xff,
          (intVal >> 8) & 0xff,
          (intVal >> 16) & 0xff,
          (intVal >> 24) & 0xff,
        ]);
      }
      case CounterType.Long: {
        return bigintToBytesLE(this.value as bigint);
      }
      default:
        throw new YorkieError(
          Code.ErrUnimplemented,
          `unimplemented type: ${this.valueType}`,
        );
    }
  }

  /**
   * `isDedup` returns whether dedup mode is enabled (derived from ValueType).
   */
  public isDedup(): boolean {
    return this.valueType === CounterType.IntDedup;
  }

  /**
   * `increaseDedup` increases the counter using HLL-based dedup.
   * Only updates the value if the actor is new (not seen before).
   */
  public increaseDedup(v: Primitive, actor: string): CRDTCounter {
    if (!this.isDedup() || !this.hll) {
      return this.increase(v);
    }
    if (!this.isNumericType() || !v.isNumericType()) {
      throw new TypeError(`Unsupported type of value: ${typeof v.getValue()}`);
    }
    if (!actor) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'dedup counter requires actor',
      );
    }
    const val = v.getValue();
    const isUnit = v.getType() === PrimitiveType.Long ? val === 1n : val === 1;
    if (!isUnit) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'dedup counter only supports increment by 1',
      );
    }
    if (this.hll.add(actor)) {
      this.recomputeValue();
    }
    return this;
  }

  /**
   * `hllBytes` returns the HLL register bytes, or undefined if not in dedup mode.
   */
  public hllBytes(): Uint8Array | undefined {
    return this.hll?.toBytes();
  }

  /**
   * `restoreHLL` restores the HLL state from serialized bytes.
   */
  public restoreHLL(data: Uint8Array): void {
    if (!this.hll) {
      this.hll = new HLL();
    }
    this.hll.restore(data);
    this.recomputeValue();
  }

  /**
   * `recomputeValue` updates the counter value from the HLL cardinality estimate.
   */
  private recomputeValue(): void {
    if (!this.hll) return;
    this.value = this.hll.count();
  }

  /**
   * `increase` increases numeric data.
   * Dedup counters must use increaseDedup() instead.
   */
  public increase(v: Primitive): CRDTCounter {
    if (this.isDedup()) {
      throw new YorkieError(
        Code.ErrInvalidArgument,
        'dedup counter requires actor, use increaseDedup()',
      );
    }

    /**
     * `checkNumericType` checks if the given target is a numeric type.
     */
    function checkNumericType(target: Primitive | CRDTCounter): void {
      if (!target.isNumericType()) {
        throw new TypeError(
          `Unsupported type of value: ${typeof target.getValue()}`,
        );
      }
    }
    checkNumericType(this);
    checkNumericType(v);

    if (this.valueType === CounterType.Long) {
      const delta =
        typeof v.getValue() === 'bigint'
          ? (v.getValue() as bigint)
          : BigInt(Math.trunc(v.getValue() as number));
      this.value = BigInt.asIntN(64, (this.value as bigint) + delta);
    } else {
      if (v.getType() === PrimitiveType.Long) {
        this.value = bigintToInt32(
          BigInt(this.value as number) + (v.getValue() as bigint),
        );
      } else {
        this.value = bigintToInt32(
          BigInt(
            (this.value as number) + removeDecimal(v.getValue() as number),
          ),
        );
      }
    }

    return this;
  }
}
